import React, { FC, useContext } from 'react';

import { Container } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CourseIcon from '../../assets/icons/CourseIcon';
import { IKImage } from '../../components/IKImage';
import PathwayLogo from '../../components/TestResults/components/PathwayLogo/PathwayLogo';
import { CustomButton } from '../../components/UI/CustomButton';
import { H2, BodyText, ButtonText } from '../../components/UI/Texts';
import { TenantCtx } from '../../containers/CommonLayout/CommonLayout';
import styled from '../../styled';
import { BIG_MARGIN_PX, colorsPalette, MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '../../theme';

interface CourseSlideProps {
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  slug?: string | null;
  icon?: string | null;
  isDarkSlide?: boolean;
}
const CourseSlide: FC<CourseSlideProps> = ({
  title,
  description,
  imageUrl,
  slug,
  icon,
  isDarkSlide = true,
}) => {
  const { tenant } = useContext(TenantCtx);
  const { t } = useTranslation();

  return (
    <Wrapper>
      <BgImageWrapper isDarkSlide={isDarkSlide}>
        <BgImage path={imageUrl} />
      </BgImageWrapper>
      <StyledContainer>
        <ContentWrapper>
          <Subtitle>{t('block.coursesBlock.featuredCourse')}</Subtitle>
          <TitleRow>
            <StyledPathwayLogo
              circleUrl={tenant?.circle_image_url}
              icon={icon || <CourseIcon secondaryColor={alpha(colorsPalette.white, 0.33)} />}
            />
            <H2>{title}</H2>
          </TitleRow>
          <Description>{description}</Description>
          <Link to={`/courses/${slug}`}>
            <StyledCustomButton>{t('button.exploreCourse')}</StyledCustomButton>
          </Link>
        </ContentWrapper>
      </StyledContainer>
    </Wrapper>
  );
};

export default CourseSlide;

const Wrapper = styled('div')`
  padding: 200px 0 ${BIG_MARGIN_PX} 0;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 100px 108px;
  }
`;

const BgImageWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDarkSlide',
})`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  height: 390px;
  overflow: hidden;
  &::before {
    content: '';
    width: 100%;
    height: 180px;
    position: absolute;
    bottom: 0;
    background: linear-gradient(0deg, ${colorsPalette.blueDark} 0%, rgba(27, 25, 37, 0.0001) 100%);
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    background: linear-gradient(
      0deg,
      ${({ isDarkSlide }) => (isDarkSlide ? colorsPalette.blackSecondary : colorsPalette.blueDark)}
        0%,
      rgba(27, 25, 37, 0.0001) 100%
    );
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 100%;
    &::before {
      background: linear-gradient(
        90deg,
        ${({ isDarkSlide }) =>
            alpha(isDarkSlide ? colorsPalette.blueDark : colorsPalette.blackSecondary, 0.95)}
          31.77%,
        rgba(27, 25, 37, 0.0001) 100%
      );
      height: 100%;
    }
  }
`;

const BgImage = styled(IKImage, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  object-fit: cover;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 100%;
  }
`;

const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  display: flex;
  align-items: center;
  margin: 0;
  ${({ theme }) => theme.breakpoints.up('xl')} {
    margin: 0 auto;
  }
`;

const ContentWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  z-index: 2;
  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 600px;
    height: 420px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const Subtitle = styled(ButtonText, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  text-transform: uppercase;
  color: ${alpha(colorsPalette.white, 0.3)};
  font-weight: bold;
  margin-bottom: ${SMALL_MARGIN_PX};
`;

const TitleRow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  margin-bottom: ${SMALL_MARGIN_PX};
  display: flex;
  align-items: center;
`;

const StyledPathwayLogo = styled(PathwayLogo)`
  width: 36px;
  min-width: 36px;
  height: 36px;
  margin-right: ${SMALL_MARGIN_PX};
  margin-bottom: 0 !important;
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 54px;
    min-width: 54px;
    height: 54px;
  }
`;

const Description = styled(BodyText, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;

const StyledCustomButton = styled(CustomButton, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: unset;
  }
`;
